import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

function todayDateString() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function StatusBadge({ status, isZh }) {
  const styles = {
    released: { backgroundColor: 'rgba(201,169,97,0.2)', color: '#c9a961', label: isZh ? '已发放' : 'Released' },
    pending: { backgroundColor: 'rgba(201,169,97,0.05)', color: '#b8a68f', label: isZh ? '待发放' : 'Pending' },
    flagged: { backgroundColor: 'rgba(217,119,6,0.15)', color: '#d97706', label: isZh ? '⚠ 已标记' : '⚠ Flagged' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className="px-2 py-1 rounded text-body-xs font-medium" style={{ backgroundColor: s.backgroundColor, color: s.color }}>
      {s.label}
    </span>
  );
}

function NumberEditor({ label, value, onSave, saving, suffix, min = 0, max = 100, isZh }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const startEdit = () => {
    setEditValue(value === null || value === undefined ? '' : String(value));
    setEditing(true);
  };

  const save = async () => {
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed < min || (max !== null && parsed > max)) {
      alert(isZh
        ? `请输入一个${max !== null ? `${min} 到 ${max} 之间的` : `不小于 ${min} 的`}数字`
        : `Please enter a number${max !== null ? ` between ${min} and ${max}` : ` >= ${min}`}`);
      return;
    }
    await onSave(parsed);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
      <span className="text-body-sm" style={{ color: '#b8a68f' }}>{label}</span>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            min={min}
            max={max ?? undefined}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            disabled={saving}
            className="w-24 px-2 py-1 text-sm rounded border"
            style={{ borderColor: '#3d5a4a' }}
          />
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {saving ? (isZh ? '保存中...' : 'Saving...') : (isZh ? '保存' : 'Save')}
          </button>
          <button
            onClick={() => setEditing(false)}
            disabled={saving}
            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
            style={{ backgroundColor: '#3d5a4a', color: '#b8a68f' }}
          >
            {isZh ? '取消' : 'Cancel'}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="font-medium" style={{ color: '#c9a961' }}>{value === null || value === undefined ? '—' : value}{value !== null && value !== undefined ? (suffix !== undefined ? suffix : '%') : ''}</span>
          <button
            onClick={startEdit}
            className="px-3 py-1 text-xs rounded font-medium"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {isZh ? '编辑' : 'Edit'}
          </button>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
      style={
        active
          ? { color: '#0d2818', backgroundColor: '#c9a961' }
          : { color: '#c9a961', backgroundColor: 'transparent' }
      }
    >
      {children}
    </button>
  );
}

// Cash/Product Points for a record: released records report their actual
// stored split; pending records derive it from the current split ratio,
// same convention as DirectReferral.jsx/Monthly.jsx/TeamBonus.jsx.
function cashCredit(r, splitCashPercent) {
  if (r.status === 'released') {
    return { cash: Number(r.cash_amount || 0), credit: Number(r.credit_amount || 0) };
  }
  const amt = Number(r.amount || 0);
  return { cash: amt * (splitCashPercent / 100), credit: amt * (1 - splitCashPercent / 100) };
}

// ---------------------------------------------------------------------
// Custom hook: list + release for one payout system. Direct Referral is
// not period-scoped (periodScoped: false) -- its list route takes no
// year/month query params, per the brief ("Direct Referral has no period
// concept").
// ---------------------------------------------------------------------
function usePayoutSystem({ token, listPath, releasePath, idParam, periodScoped, date }) {
  const [run, setRun] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [statusTab, setStatusTab] = useState('pending');

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const url = periodScoped
        ? `${process.env.REACT_APP_API_URL}${listPath}?date=${date}`
        : `${process.env.REACT_APP_API_URL}${listPath}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load');
      }
      setRun(data.run !== undefined ? data.run : null);
      setRecords(data.records || data.commissions || []);
      setSelectedIds({});
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, listPath, periodScoped, date]);

  const release = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    setReleasing(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}${releasePath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [idParam]: ids }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to release');
      }
      const failures = (data.results || []).filter((r) => !r.success);
      const successes = (data.results || []).filter((r) => r.success);
      setStatusMessage(
        failures.length > 0
          ? `Released ${successes.length}, ${failures.length} failed: ${failures.map((f) => f.message).join('; ')}`
          : `Released ${successes.length} record${successes.length === 1 ? '' : 's'}.`
      );
      await fetchList();
    } catch (err) {
      setError(err.message || 'Failed to release');
    } finally {
      setReleasing(false);
    }
  }, [token, releasePath, idParam, fetchList]);

  return {
    run, records, loading, error, statusMessage, releasing,
    selectedIds, setSelectedIds, statusTab, setStatusTab,
    fetchList, release,
  };
}

// ---------------------------------------------------------------------
// Custom hook: rates fetch/save for one payout system.
// ---------------------------------------------------------------------
function useRatesSystem({ token, ratesPath, updateRatePath, updateStarRatePath }) {
  const [payoutRates, setPayoutRates] = useState([]);
  const [starRates, setStarRates] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState('');

  const fetchRates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}${ratesPath}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load rates');
      }
      setPayoutRates(data.payout_rates || []);
      setStarRates(data.star_rates || []);
    } catch (err) {
      setError(err.message || 'Failed to load rates');
    }
  }, [token, ratesPath]);

  const saveRate = useCallback(async (rateKey, value, path) => {
    setSavingKey(rateKey);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}${path || updateRatePath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rate_key: rateKey, rate_percent: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update rate');
      }
      await fetchRates();
    } catch (err) {
      setError(err.message || 'Failed to update rate');
    } finally {
      setSavingKey(null);
    }
  }, [token, updateRatePath, fetchRates]);

  const saveStarRate = useCallback(async (star, value) => {
    setSavingKey(`star-${star}`);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}${updateStarRatePath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ star, percent: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update star rate');
      }
      await fetchRates();
    } catch (err) {
      setError(err.message || 'Failed to update star rate');
    } finally {
      setSavingKey(null);
    }
  }, [token, updateStarRatePath, fetchRates]);

  return { payoutRates, starRates, savingKey, error, fetchRates, saveRate, saveStarRate };
}

// ---------------------------------------------------------------------
// Shared table for one system's pending/released records.
// ---------------------------------------------------------------------
function PayoutTable({
  sys, isBjoechia, isZh, tt, showSplit, splitCashPercent,
  renderRecipient, extraHeader, renderExtra, emptyHint,
}) {
  const pending = useMemo(() => sys.records.filter((r) => r.status === 'pending'), [sys.records]);
  const released = useMemo(() => sys.records.filter((r) => r.status === 'released'), [sys.records]);
  const tabRecords = sys.statusTab === 'pending' ? pending : released;
  const allPendingSelected = pending.length > 0 && pending.every((r) => sys.selectedIds[r.id]);
  const selectedCount = Object.values(sys.selectedIds).filter(Boolean).length;

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      sys.setSelectedIds({});
    } else {
      const next = {};
      pending.forEach((r) => { next[r.id] = true; });
      sys.setSelectedIds(next);
    }
  };

  const toggleSelect = (id) => {
    sys.setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <>
      {sys.error && (
        <div className="border rounded-lg p-3 mb-4 text-body-sm" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
          {sys.error}
        </div>
      )}
      {sys.statusMessage && (
        <div className="border rounded-lg p-3 mb-4 text-body-sm" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#b8a68f' }}>
          {sys.statusMessage}
        </div>
      )}

      <div className="flex gap-2 mb-4 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
        <TabButton active={sys.statusTab === 'pending'} onClick={() => sys.setStatusTab('pending')}>{tt('Pending', '待发放')} ({pending.length})</TabButton>
        <TabButton active={sys.statusTab === 'released'} onClick={() => sys.setStatusTab('released')}>{tt('Released', '已发放')} ({released.length})</TabButton>
      </div>

      {sys.statusTab === 'pending' && pending.length > 0 && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <label className="flex items-center gap-2 text-body-sm" style={{ color: '#b8a68f' }}>
            <input type="checkbox" checked={allPendingSelected} onChange={toggleSelectAll} />
            {tt('Select all pending', '全选待发放')}
          </label>
          <button
            type="button"
            onClick={() => sys.release(Object.keys(sys.selectedIds).filter((id) => sys.selectedIds[id]))}
            disabled={sys.releasing || selectedCount === 0 || !isBjoechia}
            title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
            className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {sys.releasing ? tt('Releasing...', '发放中...') : tt(`Release Selected (${selectedCount})`, `发放所选 (${selectedCount})`)}
          </button>
        </div>
      )}

      {sys.loading ? (
        <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
      ) : sys.records.length === 0 ? (
        <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <p style={{ color: '#b8a68f' }}>{emptyHint}</p>
        </div>
      ) : tabRecords.length === 0 ? (
        <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <p style={{ color: '#b8a68f' }}>{tt(`No ${sys.statusTab} records.`, `暂无${sys.statusTab === 'pending' ? '待发放' : '已发放'}记录。`)}</p>
        </div>
      ) : (
        <div className="overflow-x-auto mb-8">
          <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
            <thead>
              <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                <th className="p-2"></th>
                <th className="p-2">{tt('Recipient', '接收人')}</th>
                {extraHeader && <th className="p-2">{extraHeader}</th>}
                <th className="p-2">{tt('Amount', '金额')}</th>
                {showSplit && <th className="p-2">{tt('Cash Points', '现金积分')}</th>}
                {showSplit && <th className="p-2">{tt('Product Points', '产品积分')}</th>}
                <th className="p-2">{tt('Status', '状态')}</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {tabRecords.map((r) => {
                const { cash, credit } = showSplit ? cashCredit(r, splitCashPercent) : { cash: 0, credit: 0 };
                return (
                  <tr key={r.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                    <td className="p-2">
                      {r.status === 'pending' && (
                        <input type="checkbox" checked={!!sys.selectedIds[r.id]} onChange={() => toggleSelect(r.id)} />
                      )}
                    </td>
                    <td className="p-2" style={{ color: '#c9a961' }}>{renderRecipient(r)}</td>
                    {extraHeader && <td className="p-2">{renderExtra(r)}</td>}
                    <td className="p-2" style={{ color: '#c9a961' }}>{Number(r.amount).toFixed(2)} CP</td>
                    {showSplit && <td className="p-2">{cash.toFixed(2)}</td>}
                    {showSplit && <td className="p-2">{credit.toFixed(2)}</td>}
                    <td className="p-2"><StatusBadge status={r.status} isZh={isZh} /></td>
                    <td className="p-2">
                      {r.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => sys.release([r.id])}
                          disabled={sys.releasing || !isBjoechia}
                          title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                          className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                          style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                        >
                          {tt('Release', '发放')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function MemberRewards() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isBjoechia = (user?.wellis_id || '').toUpperCase() === 'WELLIS-BJOECHIA';

  const [activeSection, setActiveSection] = useState('direct');
  const [date, setDate] = useState(todayDateString());
  const [runAllLoading, setRunAllLoading] = useState(false);
  const [runAllMessage, setRunAllMessage] = useState('');
  const [runAllError, setRunAllError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const directReferral = usePayoutSystem({
    token, listPath: '/api/admin/direct-referral/list', releasePath: '/api/admin/direct-referral/release',
    idParam: 'commission_ids', periodScoped: false, date,
  });
  const starRanking = usePayoutSystem({
    token, listPath: '/api/admin/monthly-passive/list', releasePath: '/api/admin/monthly-passive/release',
    idParam: 'record_ids', periodScoped: true, date,
  });
  const passive = usePayoutSystem({
    token, listPath: '/api/admin/company-pool/list', releasePath: '/api/admin/company-pool/release',
    idParam: 'record_ids', periodScoped: true, date,
  });
  const teamBonus = usePayoutSystem({
    token, listPath: '/api/admin/team-bonus-v2/list', releasePath: '/api/admin/team-bonus-v2/release',
    idParam: 'record_ids', periodScoped: true, date,
  });

  const directRates = useRatesSystem({
    token, ratesPath: '/api/admin/direct-referral/rates', updateRatePath: '/api/admin/direct-referral/update-rate',
  });
  const starRates = useRatesSystem({
    token, ratesPath: '/api/admin/monthly-passive/rates', updateRatePath: '/api/admin/monthly-passive/update-rate',
    updateStarRatePath: '/api/admin/monthly-passive/update-star-rate',
  });
  const passiveRates = useRatesSystem({
    token, ratesPath: '/api/admin/company-pool/rates', updateRatePath: '/api/admin/company-pool/update-rate',
  });
  const teamRates = useRatesSystem({
    token, ratesPath: '/api/admin/team-bonus-v2/rates', updateRatePath: '/api/admin/company-pool/update-rate',
    updateStarRatePath: '/api/admin/team-bonus-v2/update-star-rate',
  });

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    directReferral.fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    starRanking.fetchList();
    passive.fetchList();
    teamBonus.fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, date]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    directRates.fetchRates();
    starRates.fetchRates();
    passiveRates.fetchRates();
    teamRates.fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const handleRunAll = async () => {
    setRunAllLoading(true);
    setRunAllError('');
    setRunAllMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/member-rewards/run-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ run_date: date }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to run rewards', '执行奖励计算失败'));
      }
      const r = data.results;
      const line = (label, result) => `${label}: ${result.success ? (isZh ? '✓ ' : 'OK — ') + (result.message || '') : (isZh ? '✗ 失败 — ' : 'Failed — ') + (result.message || '')}`;
      setRunAllMessage([
        line(tt('Direct Referral', '直推佣金'), r.direct_referral),
        line(tt('Team Booster', '团队激励'), r.star_ranking),
        line(tt('Passive', '被动'), r.passive),
        line(tt('Team Bonus', '团队奖金'), r.team_bonus),
      ].join('  |  '));
      await Promise.all([directReferral.fetchList(), starRanking.fetchList(), passive.fetchList(), teamBonus.fetchList()]);
    } catch (err) {
      setRunAllError(err.message || tt('Failed to run rewards', '执行奖励计算失败'));
    } finally {
      setRunAllLoading(false);
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return null;
  }

  const starSplitPct = (() => {
    const r = starRates.payoutRates.find((x) => x.rate_key === 'PASSIVE_MONTHLY_SPLIT_CASH_PERCENT');
    return r ? Number(r.rate_percent) : 50;
  })();
  const passiveSplitPct = (() => {
    const r = passiveRates.payoutRates.find((x) => x.rate_key === 'COMPANY_POOL_SPLIT_CASH_PERCENT');
    return r ? Number(r.rate_percent) : 50;
  })();
  const teamSplitPct = (() => {
    const r = teamRates.payoutRates.find((x) => x.rate_key === 'TEAM_BONUS_V2_SPLIT_CASH_PERCENT');
    return r ? Number(r.rate_percent) : 50;
  })();

  const sponsorL1 = directRates.payoutRates.find((r) => r.rate_key === 'SPONSOR_L1');
  const sponsorL2 = directRates.payoutRates.find((r) => r.rate_key === 'SPONSOR_L2');
  const passiveMonthlyRate = starRates.payoutRates.find((r) => r.rate_key === 'PASSIVE_MONTHLY');
  const companyPoolRate = passiveRates.payoutRates.find((r) => r.rate_key === 'COMPANY_POOL_RATE');

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h1 className="text-headline-lg font-bold" style={{ color: '#c9a961' }}>
          {tt('Member Rewards', '会员奖励')}
        </h1>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="date"
            value={date}
            onChange={(e) => {
              if (e.target.value) setDate(e.target.value);
            }}
            className="px-3 py-2 rounded-lg border text-body-sm"
            style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#c9a961' }}
          />
          <button
            type="button"
            onClick={handleRunAll}
            disabled={runAllLoading || !isBjoechia}
            title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
            className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {runAllLoading ? tt('Running...', '执行中...') : tt('Run Rewards', '执行奖励')}
          </button>
        </div>
      </div>
      {!isBjoechia && (
        <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
          {tt('Only WELLIS-BJOECHIA can run Calculate, Release, or Run Rewards. You can still view all data and rates below.', '只有 WELLIS-BJOECHIA 可以执行"计算"、"发放"或"执行奖励"。您仍可查看以下所有数据与比例设置。')}
        </p>
      )}
      <p className="text-body-xs mb-6" style={{ color: '#999999' }}>
        {tt(
          'The date picker and Run Rewards apply to Team Booster, Passive, and Team Bonus, which all run once per calendar day. Direct Referral has no period concept — its section always shows recent commissions regardless of the selected day.',
          '日期选择器与"执行奖励"适用于团队激励、被动和团队奖金，三者均按日计算。直推佣金没有周期概念——其部分始终显示近期佣金，与所选日期无关。'
        )}
      </p>

      {runAllError && (
        <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
          {runAllError}
        </div>
      )}
      {runAllMessage && (
        <div className="border rounded-lg p-4 mb-4 text-body-sm" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#b8a68f' }}>
          {runAllMessage}
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b flex-wrap" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
        <TabButton active={activeSection === 'direct'} onClick={() => setActiveSection('direct')}>{tt('Direct Referral', '直推佣金')}</TabButton>
        <TabButton active={activeSection === 'star'} onClick={() => setActiveSection('star')}>{tt('Team Booster', '团队激励')}</TabButton>
        <TabButton active={activeSection === 'passive'} onClick={() => setActiveSection('passive')}>{tt('Passive', '被动')}</TabButton>
        <TabButton active={activeSection === 'team'} onClick={() => setActiveSection('team')}>{tt('Team Bonus', '团队奖金')}</TabButton>
        <TabButton active={activeSection === 'rates'} onClick={() => setActiveSection('rates')}>{tt('Rates', '比例设置')}</TabButton>
      </div>

      {activeSection === 'direct' && (
        <PayoutTable
          sys={directReferral}
          isBjoechia={isBjoechia}
          isZh={isZh}
          tt={tt}
          showSplit={false}
          splitCashPercent={0}
          renderRecipient={(c) => memberLabel(c.recipient)}
          extraHeader={tt('Referred Member / Tier', '被推荐人 / 级别')}
          renderExtra={(c) => `${memberLabel(c.referred)} — ${tt('Tier', '级别')} ${c.tier} (${c.rate_percent}%)`}
          emptyHint={tt('No commissions yet.', '暂无佣金记录。')}
        />
      )}

      {activeSection === 'star' && (
        <PayoutTable
          sys={starRanking}
          isBjoechia={isBjoechia}
          isZh={isZh}
          tt={tt}
          showSplit
          splitCashPercent={starSplitPct}
          renderRecipient={(r) => `${'★'.repeat(r.recipient_ranking_snapshot || 0)} ${memberLabel(r.recipient)}`}
          extraHeader={tt('Downline / Star %', '下线 / 星级 %')}
          renderExtra={(r) => `${memberLabel(r.buyer)} — ${r.star_percent_snapshot}%`}
          emptyHint={tt('No records yet.', '暂无记录。')}
        />
      )}

      {activeSection === 'passive' && (
        <PayoutTable
          sys={passive}
          isBjoechia={isBjoechia}
          isZh={isZh}
          tt={tt}
          showSplit
          splitCashPercent={passiveSplitPct}
          renderRecipient={(r) => memberLabel(r.recipient)}
          extraHeader={tt('Package', '配套')}
          renderExtra={(r) => `${r.package_code} (${Number(r.package_price).toFixed(2)} CP)`}
          emptyHint={tt('No records yet.', '暂无记录。')}
        />
      )}

      {activeSection === 'team' && (
        <PayoutTable
          sys={teamBonus}
          isBjoechia={isBjoechia}
          isZh={isZh}
          tt={tt}
          showSplit
          splitCashPercent={teamSplitPct}
          renderRecipient={(r) => `${'★'.repeat(r.recipient_ranking_snapshot || 0)} ${memberLabel(r.recipient)}`}
          extraHeader={tt('Team Passive Total / Star %', '团队被动总额 / 星级 %')}
          renderExtra={(r) => `${Number(r.team_passive_total_snapshot || 0).toFixed(2)} CP — ${r.star_percent_snapshot}%`}
          emptyHint={tt('No records yet.', '暂无记录。')}
        />
      )}

      {activeSection === 'rates' && (
        <div className="space-y-8">
          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Direct Referral Rates', '直推佣金比例')}</h3>
            {directRates.error && <p className="text-body-sm mb-3" style={{ color: '#c94961' }}>{directRates.error}</p>}
            <div className="space-y-3">
              <NumberEditor
                label={tt('Sponsor Level 1', '推荐一级')}
                value={sponsorL1 ? Number(sponsorL1.rate_percent) : null}
                onSave={(v) => directRates.saveRate('SPONSOR_L1', v)}
                saving={directRates.savingKey === 'SPONSOR_L1'}
                isZh={isZh}
              />
              <NumberEditor
                label={tt('Sponsor Level 2', '推荐二级')}
                value={sponsorL2 ? Number(sponsorL2.rate_percent) : null}
                onSave={(v) => directRates.saveRate('SPONSOR_L2', v)}
                saving={directRates.savingKey === 'SPONSOR_L2'}
                isZh={isZh}
              />
            </div>
          </div>

          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Team Booster Rates', '团队激励比例')}</h3>
            {starRates.error && <p className="text-body-sm mb-3" style={{ color: '#c94961' }}>{starRates.error}</p>}
            <div className="space-y-3 mb-4">
              <NumberEditor
                label={tt('Passive Rate', '被动比例')}
                value={passiveMonthlyRate ? Number(passiveMonthlyRate.rate_percent) : null}
                onSave={(v) => starRates.saveRate('PASSIVE_MONTHLY', v)}
                saving={starRates.savingKey === 'PASSIVE_MONTHLY'}
                isZh={isZh}
              />
              <NumberEditor
                label={tt('Cash Points Split', '现金积分分配比例')}
                value={(() => { const r = starRates.payoutRates.find((x) => x.rate_key === 'PASSIVE_MONTHLY_SPLIT_CASH_PERCENT'); return r ? Number(r.rate_percent) : null; })()}
                onSave={(v) => starRates.saveRate('PASSIVE_MONTHLY_SPLIT_CASH_PERCENT', v)}
                saving={starRates.savingKey === 'PASSIVE_MONTHLY_SPLIT_CASH_PERCENT'}
                isZh={isZh}
              />
            </div>
            <h4 className="text-body-sm font-bold mb-3" style={{ color: '#c9a961' }}>{tt('Star Rates', '星级比例')}</h4>
            <div className="space-y-3">
              {starRates.starRates.map((sr) => (
                <NumberEditor
                  key={sr.star}
                  label={tt(`${sr.star} star`, `${sr.star} 星`)}
                  value={Number(sr.percent)}
                  onSave={(v) => starRates.saveStarRate(sr.star, v)}
                  saving={starRates.savingKey === `star-${sr.star}`}
                  isZh={isZh}
                />
              ))}
            </div>
          </div>

          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Passive Rates', '被动比例')}</h3>
            {passiveRates.error && <p className="text-body-sm mb-3" style={{ color: '#c94961' }}>{passiveRates.error}</p>}
            <div className="space-y-3">
              <NumberEditor
                label={tt('Passive Distribution Rate', '被动分配比例')}
                value={companyPoolRate ? Number(companyPoolRate.rate_percent) : null}
                onSave={(v) => passiveRates.saveRate('COMPANY_POOL_RATE', v)}
                saving={passiveRates.savingKey === 'COMPANY_POOL_RATE'}
                isZh={isZh}
              />
              <NumberEditor
                label={tt('Cash Points Split', '现金积分分配比例')}
                value={(() => { const r = passiveRates.payoutRates.find((x) => x.rate_key === 'COMPANY_POOL_SPLIT_CASH_PERCENT'); return r ? Number(r.rate_percent) : null; })()}
                onSave={(v) => passiveRates.saveRate('COMPANY_POOL_SPLIT_CASH_PERCENT', v)}
                saving={passiveRates.savingKey === 'COMPANY_POOL_SPLIT_CASH_PERCENT'}
                isZh={isZh}
              />
            </div>
            <p className="text-body-xs mt-3" style={{ color: '#999999' }}>
              {tt('Package allocation weights and per-package passive caps are still edited on the Passive page.', '配套分配权重及各配套被动收益上限仍在"被动"页面中编辑。')}
            </p>
          </div>

          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Team Bonus Rates', '团队奖金比例')}</h3>
            {teamRates.error && <p className="text-body-sm mb-3" style={{ color: '#c94961' }}>{teamRates.error}</p>}
            <div className="space-y-3 mb-4">
              <NumberEditor
                label={tt('Cash Points Split', '现金积分分配比例')}
                value={(() => { const r = teamRates.payoutRates.find((x) => x.rate_key === 'TEAM_BONUS_V2_SPLIT_CASH_PERCENT'); return r ? Number(r.rate_percent) : null; })()}
                onSave={(v) => teamRates.saveRate('TEAM_BONUS_V2_SPLIT_CASH_PERCENT', v)}
                saving={teamRates.savingKey === 'TEAM_BONUS_V2_SPLIT_CASH_PERCENT'}
                isZh={isZh}
              />
            </div>
            <h4 className="text-body-sm font-bold mb-3" style={{ color: '#c9a961' }}>{tt('Team Bonus Star Rates', '团队奖金星级比例')}</h4>
            <div className="space-y-3">
              {teamRates.starRates.map((sr) => (
                <NumberEditor
                  key={sr.star}
                  label={tt(`${sr.star} star`, `${sr.star} 星`)}
                  value={Number(sr.percent)}
                  onSave={(v) => teamRates.saveStarRate(sr.star, v)}
                  saving={teamRates.savingKey === `star-${sr.star}`}
                  isZh={isZh}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
